// The electrical schematic's EDITS (docs/claude/elecSchematic.md): every change a
// person makes to it, as data (`ElecEdit`), made by one function
// (`applyElecEdit`). Where each of a wire's figures is kept for each kind of
// wire, an edit dropped when it is back to its default, the order a drag writes
// on a cell, a text entered as typed and a default name never stored
// (docs/claude/names.md): all here. Pure (no DOM): the app makes the undo step
// around it; a drop, Delete and the wire's dialog give edits, and the tests
// make them.

import type {
  Connection,
  Drawing,
  ElecBoxAngle,
  ElecBoxEdit,
  ElecCellId,
  ElecPlantRoom,
  ElecRowId,
  ElecSchematic,
  ElecView,
  ElecWireEdit,
  ProductInstance,
  SchematicLineStyle,
  Vec2,
} from '../shared/types';
import {
  buildElecSchematic,
  clampElecJog,
  compareElecBoxesAuto,
  ELEC_COLUMN_COLORS,
  ELEC_INSTALLER_COLORS,
  ELEC_KEY,
  ELEC_NEW_BOX,
  ELEC_ROWS,
  ELEC_WIRE_THICKNESS,
  type ElecBox,
  elecBoxPart,
  elecFeedLoops,
  type ElecModel,
  elecOutOfOrder,
  elecPlantRooms,
  type ElecTr,
} from './elecSchematic';
import type { ElecLayoutBox } from './elecLayout';
import {
  applyConnEdit,
  type ConnEdit,
  type ConnSticky,
  newConnection,
} from './connEdits';
import { unreachable } from '../shared/exhaustive';

/** What an edit may change: the schematic's hand edits, a project wire's
 *  connection (its figures, its ends, in the plan too) and a view. */
export type ElecEditDrawing = Pick<Drawing, 'connections' | 'instances'> & Partial<Pick<Drawing, 'elecSchematic' | 'language'>>;

export interface ElecEditContext {
  drawing: ElecEditDrawing;
  /** The schematic as it is before the edit. */
  model: ElecModel;
  /** The schematic's own words in the drawing's language, as the model reads
   *  them (a new hand box's "New box", to place it); absent: English. */
  tr?: ElecTr;
  /** Every view's client row as it stands it by itself (`ElecLayout.clientAuto`):
   *  the app's, so a client-row order back in it in every view is dropped and
   *  one written keeps the other views as they were. Absent: only the edit's
   *  own view's (`auto`) is known. */
  clientAuto?: () => string[][];
}

/** A wire's figures as typed: absent = unchanged, null = back to its own (worked
 *  out, the schematic's, its installer's). A project wire's who / cable / length
 *  / lines are its connection's; a hand wire's its own, its ends too; an
 *  automatic or a preparation's typed over the automatic ones, `fed` its far
 *  end. How any wire is drawn is its own edit. */
export interface ElecWireFigures {
  installedBy?: 'client' | 'stenal';
  cable?: string;
  length?: number | null;
  lines?: number;
  ends?: { from: string; to: string };
  fed?: string | null;
  straight?: boolean;
  jog?: number | null;
  thickness?: number | null;
  style?: SchematicLineStyle | null;
  color?: string | null;
}

/** A change to the schematic, as a person makes it. A box dropped or added at
 *  `index` counts among the cell's boxes in the model's order — or, given
 *  `drawn`, in the order the view it was dropped in draws them, left to right
 *  (the client row stands its boxes under what each is wired to), the moved box
 *  left out. A drop or Back to its own place in the client row gives that
 *  view's own order of it (`auto`: `ElecLayout.clientAuto`). */
export type ElecEdit =
  | { kind: 'border'; row: ElecRowId; after: string; dx?: number | null; removed?: boolean }
  | { kind: 'boxAdd'; id: string; cell: ElecCellId; index?: number; drawn?: string[]; auto?: string[] }
  | { kind: 'boxElectricalLoad'; key: string; on: boolean }
  | { kind: 'boxHidden'; key: string; hidden: boolean }
  | { kind: 'boxHome'; key: string; auto?: string[] }
  | { kind: 'boxListLoads'; key: string; on: boolean }
  | { kind: 'boxMove'; key: string; cell: ElecCellId; index: number; drawn?: string[]; auto?: string[] }
  | { kind: 'boxPower'; key: string; w: number | null }
  | { kind: 'boxText'; key: string; text: string }
  | { kind: 'boxTurn'; box: Pick<ElecLayoutBox, 'angle' | 'autoAngle' | 'key'>; dir: -1 | 1 }
  | { kind: 'column'; id: string; name?: string; color?: string; hidden?: boolean }
  | { kind: 'columnAdd'; id: string; view?: ElecView }
  | { kind: 'columnMove'; id: string; by: -1 | 1 }
  | { kind: 'columnRemove'; id: string; view?: ElecView }
  | { kind: 'lightRun'; key: string; whole: boolean }
  | { kind: 'plantRoom'; id: string; name: string }
  | { kind: 'plantRoomAdd'; id: string }
  | { kind: 'plantRoomMove'; id: string; by: -1 | 1 }
  | { kind: 'plantRoomRemove'; id: string }
  | { kind: 'rowHeight'; row: ElecRowId; h: number | null }
  | { kind: 'rowHidden'; row: ElecRowId; hidden: boolean }
  | { kind: 'viewAside'; view: ElecView; which: 'legend' | 'table'; at?: Vec2 | null; hidden?: boolean }
  | { kind: 'viewColumn'; view: ElecView; id: string; shown: boolean }
  | { kind: 'viewNumbers'; view: ElecView; shown: boolean }
  | { kind: 'wire'; key: string; figures: ElecWireFigures }
  | { kind: 'wireAdd'; id: string; from: string; to: string }
  | { kind: 'wireEnd'; key: string; was: string; to: string }
  | { kind: 'wireLook'; thickness?: number; style?: SchematicLineStyle }
  | { kind: 'wireRemoved'; key: string; removed: boolean };

/** Why an edit cannot be made: onto where it already is (a box to its own slot,
 *  a wire's end onto its other end, a wire to the box it starts from) or onto
 *  what is not there (a box or a wire gone, a wire to no box); a project
 *  wire's end onto a box that is no part (a fibre kit's is its projector
 *  part's: `elecBoxPart`); the end of an automatic or a
 *  preparation's wire at the box it starts from; an automatic feed taken to a
 *  box it feeds itself, a loop (`elecFeedLoops`: junction boxes feeding each
 *  other). */
export type ElecRefusal = 'fixed' | 'loop' | 'part' | 'same';

const boxEdit = (sch: ElecSchematic, key: string): ElecBoxEdit => ((sch.boxes ??= {})[key] ??= {});
const wireEdit = (sch: ElecSchematic, key: string): ElecWireEdit => ((sch.wires ??= {})[key] ??= {});
/** The edits back at every default go, and the lists left empty; then each key
 *  that names a light group (an edit's, a hand wire's end) gets the group's
 *  lights (`lightKeys`), so what it names follows the group from then on,
 *  whichever of its lights is deleted. */
function prune(sch: ElecSchematic, m: ElecModel): void {
  for (const k of ['boxes', 'wires'] as const) {
    const map = sch[k];
    if (!map) continue;
    for (const key of Object.keys(map)) if (!Object.keys(map[key]).length) delete map[key];
    if (!Object.keys(map).length) delete sch[k];
  }
  // A column's edit with nothing but its id (a name deleted, shown again).
  if (sch.columns) {
    sch.columns = sch.columns.filter((c) => Object.keys(c).some((f) => f !== 'id'));
    if (!sch.columns.length) delete sch.columns;
  }
  const named = new Set([...Object.keys(sch.boxes ?? {}), ...Object.keys(sch.wires ?? {}), ...(sch.handWires ?? []).flatMap((h) => [h.from, h.to])]
    .map(ELEC_KEY.lightOf).filter((k): k is string => !!k));
  const lights = new Map([...m.boxes, ...m.hiddenBoxes].filter((b) => b.lightIds).map((b) => [b.key, b.lightIds!]));
  const keep: Record<string, string[]> = {};
  for (const k of named) { const ids = lights.get(k) ?? sch.lightKeys?.[k]; if (ids?.length) keep[k] = [...ids]; }
  if (Object.keys(keep).length) sch.lightKeys = keep; else delete sch.lightKeys;
}

/** Every edit the model found under an older key of its box or wire moved to
 *  its key now, and a hand wire's end at a light group by an older key to it;
 *  a hand box the model stands in the cell it was moved to, its own gone, has
 *  that cell for its own (so a move back there does not lose it). */
function adopt(sch: ElecSchematic, m: ElecModel): void {
  const move = <T>(map: Record<string, T> | undefined, from: string, to: string): void => {
    if (!map?.[from] || map[to]) return;
    map[to] = map[from];
    delete map[from];
  };
  for (const b of [...m.boxes, ...m.hiddenBoxes]) if (b.editKey) move(sch.boxes, b.editKey, b.key);
  for (const b of m.boxes) {
    const h = b.kind === 'hand' ? sch.handBoxes?.find((x) => x.id === b.handId) : undefined;
    if (!h || h.cell === b.autoCell) continue;
    h.cell = b.autoCell;
    const e = sch.boxes?.[b.key];
    if (e) { delete e.cell; if (e.order != null) e.orderIn = b.autoCell; }
  }
  for (const w of [...m.wires, ...m.removedWires]) {
    if (w.editKey) move(sch.wires, w.editKey, w.key);
    const h = w.kind === 'hand' ? sch.handWires?.find((x) => x.id === w.handId) : undefined;
    if (h) { h.from = w.from; h.to = w.to; }
  }
}
/** A wire's edits gone, its every line's (`<key>#<k>`) too; with `past`, only
 *  those of its lines after that many (the ones a smaller count leaves out). */
function dropWireEdits(sch: ElecSchematic, key: string, past?: number): void {
  for (const k of Object.keys(sch.wires ?? {})) {
    const line = k === key ? 1 : k.startsWith(`${key}#`) ? Number(k.slice(key.length + 1)) : 0;
    if (line && (past == null || line > past)) delete sch.wires![k];
  }
}
/** `v` set on `o[k]`, or the field gone when it is `none`. */
function put<T extends object, K extends keyof T>(o: T, k: K, v: T[K] | undefined | null, none?: T[K]): void {
  if (v == null || v === none) delete o[k]; else o[k] = v;
}

/** A cell's boxes, in their order. */
const cellKeys = (m: ElecModel, cell: ElecCellId): string[] => m.boxes.filter((b) => b.cell === cell).map((b) => b.key);
const insertAt = (keys: string[], i: number, key: string): string[] => [...keys.slice(0, Math.max(0, i)), key, ...keys.slice(Math.max(0, i))];
/** A cell's boxes as a drop counts among them: as its view drew them, while
 *  that list holds exactly the cell's boxes; else in the model's order. */
function dropOrder(m: ElecModel, cell: ElecCellId, drawn: string[] | undefined): string[] {
  const keys = cellKeys(m, cell), set = new Set(keys);
  return drawn && drawn.length === keys.length && drawn.every((k) => set.has(k)) ? [...drawn] : keys;
}
/** The client row stands its boxes under what each is wired to while none has
 *  a hand order, each view in its own order (`ElecLayout.clientAuto`) — not the
 *  model's automatic one: a hand order there is dropped once the views known
 *  stand the row so by themselves (`clientPairs`), else kept. */
const CLIENT_CELL: ElecCellId = 'client';
/** For each box, the boxes `autos` stand right of it, where none stands it
 *  left of them: a pair two views stand either way round no one order keeps. */
function pairsOf(autos: string[][]): Map<string, Set<string>> {
  const after = new Map<string, Set<string>>();
  for (const a of autos) a.forEach((k, i) => { const s = after.get(k) ?? after.set(k, new Set()).get(k)!; for (const j of a.slice(i + 1)) s.add(j); });
  for (const [k, s] of after) for (const j of [...s]) if (after.get(j)?.has(k)) { s.delete(j); after.get(j)!.delete(k); }
  return after;
}
/** The client row's order as the views stand it by themselves: every view's
 *  (`ElecEditContext.clientAuto`), and the edit's own view's (`auto`) over
 *  them — every pair of it, the other views' only where it does not stand them
 *  the other way round (a drop and Back in one view once flipped a pair of
 *  another for good: one order cannot keep both). Null: no view known. */
function clientPairs(ctx: ElecEditContext, auto?: string[]): Map<string, Set<string>> | null {
  const others = ctx.clientAuto?.() ?? [];
  if (!others.length && !auto) return null;
  const after = pairsOf(others);
  if (!auto) return after;
  const own = pairsOf([auto]);
  for (const [k, s] of after) for (const j of [...s]) if (own.get(j)?.has(k)) s.delete(j);
  for (const [k, s] of own) { const t = after.get(k) ?? after.set(k, new Set()).get(k)!; for (const j of s) t.add(j); }
  return after;
}
/** How many of those pairs `keys` stands the other way round: none, every view
 *  known stands it as by itself. */
function wrongPairs(keys: string[], after: Map<string, Set<string>>): number {
  let n = 0;
  keys.forEach((k, i) => { for (const j of keys.slice(0, i)) if (after.get(k)?.has(j)) n++; });
  return n;
}
/** `key` moved to where the fewest of those pairs stand wrong, among the places
 *  the view (`shown`) sees it no differently — between the same two boxes it
 *  shows, or anywhere for a box it does not show —, staying put on a tie: so
 *  an order written in one view keeps the others as they stand by themselves. */
function settleIn(keys: string[], key: string, shown: Set<string>, after: Map<string, Set<string>>): string[] {
  const i = keys.indexOf(key), rest = keys.filter((k) => k !== key);
  let lo = i, hi = i;
  if (!shown.has(key)) { lo = 0; hi = rest.length; } else {
    while (lo > 0 && !shown.has(rest[lo - 1])) lo--;
    while (hi < rest.length && !shown.has(rest[hi])) hi++;
  }
  let at = i, fewest = wrongPairs(keys, after);
  for (let j = lo; j <= hi; j++) { const n = wrongPairs(insertAt(rest, j, key), after); if (n < fewest) { fewest = n; at = j; } }
  return insertAt(rest, at, key);
}
/** The client row's boxes as a view put them (`auto`: those it shows, and
 *  `put`, the box it put there), each of the others settled (`settleIn`). */
function clientSettled(keys: string[], auto: string[] | undefined, put: string, after: Map<string, Set<string>> | null): string[] {
  if (!after || !auto) return keys;
  const shown = new Set([...auto, put]);
  let out = keys;
  for (const k of keys) if (!shown.has(k)) out = settleIn(out, k, shown, after);
  return settleIn(out, put, shown, after);
}
/** Whether the client row's boxes in this order stand, in every view known, as
 *  it stands them by itself (none known: not). */
const clientInAuto = (after: Map<string, Set<string>> | null, keys: string[]): boolean => !!after && wrongPairs(keys, after) === 0;
/** Back to its own place in the client row, as the view (`auto`) stands it by
 *  itself: before the first box it draws in its own place there that it stands
 *  right of it, then settled; a box the view does not show there, last. */
function clientHome(m: ElecModel, key: string, others: string[], auto: string[] | undefined, after: Map<string, Set<string>> | null): string[] {
  const rank = new Map((auto ?? []).map((k, i) => [k, i]));
  const now = cellKeys(m, CLIENT_CELL).filter((k) => rank.has(k));
  const out = elecOutOfOrder(now, (k) => rank.get(k)!, (k) => !!m.boxByKey.get(k)?.dragged);
  const r = rank.get(key);
  const i = r == null ? -1 : others.findIndex((k) => rank.has(k) && !out.has(k) && rank.get(k)! > r);
  const keys = insertAt(others, i < 0 ? others.length : i, key);
  return after ? settleIn(keys, key, new Set(rank.keys()), after) : keys;
}
/** Whether boxes of the model (and `extra`, one not in it yet) stand in their
 *  automatic order. */
function inAutoOrder(m: ElecModel, keys: string[], extra?: ElecBox): boolean {
  const boxes = keys.map((k) => (k === extra?.key ? extra : m.boxByKey.get(k)));
  if (boxes.some((b) => !b)) return false;
  return boxes.every((b, i) => i === 0 || compareElecBoxesAuto(boxes[i - 1]!, b!) < 0);
}
/** The boxes hidden by hand that come back in `cell` when shown again. */
const hiddenIn = (sch: ElecSchematic, m: ElecModel, cell: ElecCellId): ElecBox[] =>
  m.hiddenBoxes.filter((b) => (sch.boxes?.[b.key]?.cell ?? b.autoCell) === cell);
/** The longest run of boxes in their automatic order (what `moved` leaves out). */
function autoRun(boxes: ElecBox[]): number {
  const rank = new Map([...boxes].sort(compareElecBoxesAuto).map((b, i) => [b, i]));
  const r = boxes.map((b) => rank.get(b)!), len = r.map(() => 1);
  for (let i = 0; i < r.length; i++) for (let j = 0; j < i; j++) if (r[j] < r[i]) len[i] = Math.max(len[i], len[j] + 1);
  return Math.max(0, ...len);
}
/** A cell's boxes put in this order by hand, the hidden ones among them where
 *  each lengthens the run in automatic order (so none comes back `moved`), the
 *  box `dragged` there marked so (`ElecBoxEdit.dragged`: of two swapped, it is
 *  the one moved) — or, when the shown are in their automatic order (`auto`;
 *  absent, the model's), every hand order of the cell dropped, the hidden
 *  ones' too. */
function orderCell(sch: ElecSchematic, m: ElecModel, cell: ElecCellId, keys: string[], auto?: boolean, extra?: ElecBox, dragged?: string): void {
  const hidden = hiddenIn(sch, m, cell);
  if (auto ?? inAutoOrder(m, keys, extra)) { for (const k of [...keys, ...hidden.map((b) => b.key)]) dropHandOrder(sch, k); return; }
  const of = new Map([...m.boxes, ...hidden, ...(extra ? [extra] : [])].map((b) => [b.key, b]));
  const run = (list: string[]): number => autoRun(list.map((k) => of.get(k)).filter((b): b is ElecBox => !!b && b.autoCell === cell));
  let all = keys;
  for (const h of hidden) {
    let at = all.length, most = -1;
    if (h.autoCell === cell) for (let i = 0; i <= all.length; i++) { const n = run(insertAt(all, i, h.key)); if (n > most) { most = n; at = i; } }
    all = insertAt(all, at, h.key);
  }
  // Written for this cell: a box not moved here by hand, its own (`orderIn`).
  all.forEach((k, i) => {
    const e = boxEdit(sch, k);
    e.order = i;
    if (e.cell) delete e.orderIn; else e.orderIn = cell;
    if (k === dragged) e.dragged = true; else if (dragged) delete e.dragged;
  });
}
/** Where a box shown again goes among a row's boxes in their order (`keys`),
 *  so that no view (`views`: each its own order of the row) reads it, nor any
 *  other box, as moved — the fewest views reading it moved, then the fewest
 *  boxes whose `moved` it changes; `at` (where the model's order puts it)
 *  while that is one of the best. */
function hiddenPlace(keys: string[], key: string, at: number, views: string[][], home: (k: string) => boolean, dragged: (k: string) => boolean): number {
  const cost = (all: string[]): [number, number] => {
    let it = 0, others = 0;
    for (const v of views) {
      const rank = new Map(v.map((k, i) => [k, i]));
      if (!rank.has(key)) continue;
      const out = (list: string[]): Set<string> => elecOutOfOrder(list.filter((k) => rank.has(k) && home(k)), (k) => rank.get(k)!, dragged);
      const was = out(keys), now = out(all);
      if (now.has(key)) it++;
      for (const k of keys) if (was.has(k) !== now.has(k)) others++;
    }
    return [it, others];
  };
  let best = at, low = cost(insertAt(keys, at, key));
  for (let i = 0; i <= keys.length; i++) {
    const c = cost(insertAt(keys, i, key));
    if (c[0] < low[0] || (c[0] === low[0] && c[1] < low[1])) { low = c; best = i; }
  }
  return best;
}
/** A box's hand order gone, with the cell it was written for. */
function dropHandOrder(sch: ElecSchematic, key: string): void {
  const e = sch.boxes?.[key];
  if (e) { delete e.order; delete e.orderIn; delete e.dragged; }
}
/** What is left of a cell a box went out of: its hand order dropped once it is
 *  the automatic one (`auto`; absent, the model's), the hidden boxes' too. */
function tidyCell(sch: ElecSchematic, m: ElecModel, cell: ElecCellId, keys: string[], auto?: boolean): void {
  if (!(auto ?? inAutoOrder(m, keys))) return;
  for (const k of [...keys, ...hiddenIn(sch, m, cell).map((b) => b.key)]) dropHandOrder(sch, k);
}

/** The plant rooms to change: the stored list, else the main one stored now —
 *  with no name, so it still reads its default in the drawing's language. */
function plantRooms(sch: ElecSchematic): ElecPlantRoom[] {
  return sch.plantRooms?.length ? sch.plantRooms : (sch.plantRooms = elecPlantRooms(sch).map((p) => ({ ...p })));
}
/** A name entered as typed (names.md); empty, none: its default again. */
function enter<T extends { name?: string }>(o: T, v: string): void {
  const t = v.trim();
  if (t) o.name = t; else delete o.name;
}

function swap<T>(list: T[], i: number, by: -1 | 1): void {
  const j = i + by;
  if (i < 0 || j < 0 || j >= list.length) return;
  [list[i], list[j]] = [list[j], list[i]];
}

/** Why `edit` cannot be made, else null. */
export function elecEditRefusal(ctx: ElecEditContext, edit: ElecEdit): ElecRefusal | null {
  switch (edit.kind) {
    case 'wireEnd': return reconnect(ctx, edit, false);
    case 'wireAdd': return edit.from === edit.to || !ctx.model.boxByKey.has(edit.from) || !ctx.model.boxByKey.has(edit.to) ? 'same' : null;
    case 'boxMove': {
      const box = ctx.model.boxByKey.get(edit.key);
      if (!box) return 'same';
      return box.cell === edit.cell && dropOrder(ctx.model, box.cell, edit.drawn).indexOf(edit.key) === edit.index ? 'same' : null;
    }
    case 'wire': {
      const e = edit.figures.ends, fed = edit.figures.fed;
      if (e && e.from === e.to) return 'same';
      const w = fed ? ctx.model.wires.find((x) => x.key === edit.key) : undefined;
      return w?.kind === 'auto' && fed && elecFeedLoops(ctx.model, w.from, fed) ? 'loop' : null;
    }
    default: return null;
  }
}

/** One end of a wire, the one at box `was`, taken to box `to` (`apply` false:
 *  only whether it can be). */
function reconnect(ctx: ElecEditContext, edit: Extract<ElecEdit, { kind: 'wireEnd' }>, apply: boolean): ElecRefusal | null {
  const d = ctx.drawing, { key, was, to } = edit;
  const w = ctx.model.wires.find((x) => x.key === key), box = ctx.model.boxByKey.get(to);
  if (!w || !box || (was !== w.from && was !== w.to)) return 'same';
  const other = was === w.from ? w.to : w.from;
  if (to === other || to === was) return 'same';
  const which: 'from' | 'to' = was === w.from ? 'from' : 'to';
  switch (w.kind) {
    case 'hand': {
      const h = d.elecSchematic?.handWires?.find((x) => x.id === w.handId);
      if (!h) return 'same';
      if (apply) h[which] = to;
      return null;
    }
    case 'project': {
      const c = d.connections.find((x) => x.id === w.connectionId);
      const inst = c && elecBoxPart(d, box, d.instances.find((i) => i.id === (which === 'from' ? c.to : c.from)));
      if (!c || !inst) return 'part';
      if (!apply) return null;
      // The schematic's from / to are the connection's own. A height typed for
      // the end was the old part's: it stands at the new part's point.
      applyConnEdit({ drawing: d }, c, { kind: 'reglue', which, id: inst.id, end: elecEnd(inst) });
      applyConnEdit({ drawing: d }, c, { kind: 'pointZ', i: which === 'from' ? 0 : (c.waypoints?.length ?? 0) + 1, z: null });
      return null;
    }
    case 'auto': case 'prep': {
      if (which === 'from') return 'fixed';
      if (w.kind === 'auto' && elecFeedLoops(ctx.model, w.from, to)) return 'loop';
      if (apply) put(wireEdit((d.elecSchematic ??= {}), w.first ?? key), 'to', to, ownFeed(ctx, w));
      return null;
    }
    default: return unreachable(w.kind, 'wire kind');
  }
}

/** The box an automatic wire is fed from by the rule (decision 26), a
 *  preparation's wire its own part: where its far end is back at automatic. */
function ownFeed(ctx: ElecEditContext, w: ElecModel['wires'][number]): string | undefined {
  if (w.kind === 'prep') { const inst = ctx.model.boxByKey.get(w.from)?.instId; return inst ? ELEC_KEY.part(inst) : undefined; }
  if (w.kind !== 'auto') return undefined;
  // The schematic as it is, but this wire's feed: the rule's.
  const at = w.first ?? w.key, sch = ctx.drawing.elecSchematic ?? {}, own = { ...sch.wires?.[at] };
  delete own.to;
  const d = { ...ctx.drawing, elecSchematic: { ...sch, wires: { ...sch.wires, [at]: own } } } as Drawing;
  return buildElecSchematic(d, { tr: ctx.tr }).wires.find((x) => x.key === at)?.to;
}

/** A connection's end on a part: glued to its first electrical connection
 *  point, else (absent) at its middle. */
function elecEnd(inst: ProductInstance): Connection['fromEnd'] {
  const port = inst.ports?.find((p) => p.type === 'electrical');
  return port ? { port: port.id } : undefined;
}

/** What a wire drawn on the schematic is born as: electrical, straight, no pipe. */
const ELEC_WIRE: ConnSticky = { type: 'electrical', route: 'straight', pipe: '', typed: true };

/** A wire drawn by hand between two parts' boxes is a connection of the
 *  drawing — on the plan, in 3D, in the schedule, numbered E as the plan
 *  numbers it —: electrical, from the first box's part to the second's, each
 *  end glued to its part's first electrical point (else its middle). Null when
 *  either box is no part (a light but a fibre kit — its projector part's,
 *  `elecBoxPart` —, a client provision, a box added by hand):
 *  that wire is the schematic's own (S…). */
export function elecPartConnection(d: Pick<Drawing, 'instances'>, model: ElecModel, from: string, to: string, id: string): Connection | null {
  const partOf = (key: string, near?: ProductInstance): ProductInstance | undefined => elecBoxPart(d, model.boxByKey.get(key), near);
  // A fibre kit's box of several projectors: the one nearest the other end's part.
  let a = partOf(from), b = partOf(to);
  if (!a && b) a = partOf(from, b);
  if (a && !b) b = partOf(to, a);
  if (!a || !b || a === b) return null;
  // Born as any connection is, electrical whatever the plan's last one was.
  return newConnection({ drawing: { connections: [], instances: d.instances } }, { id, from: a.id, to: b.id, fromEnd: elecEnd(a), toEnd: elecEnd(b) }, ELEC_WIRE);
}

/** A wire's figures typed: each where that wire keeps it. */
function setWireFigures(ctx: ElecEditContext, sch: ElecSchematic, key: string, f: ElecWireFigures): void {
  const m = ctx.model, w = m.wires.find((x) => x.key === key);
  if (!w) return;
  const conn = w.kind === 'project' ? ctx.drawing.connections.find((c) => c.id === w.connectionId) : undefined;
  const hand = w.kind === 'hand' ? sch.handWires?.find((h) => h.id === w.handId) : undefined;
  const own = (): ElecWireEdit => wireEdit(sch, key);
  // The wire's figures and its count: its first line's, for all its lines.
  const head = (): ElecWireEdit => wireEdit(sch, w.first ?? key);
  const lines = f.lines == null ? undefined : Math.max(1, Math.round(f.lines));
  const length = f.length == null ? f.length : Math.max(0, Math.round(f.length));
  if (conn) {
    // The connection's own rules (connEdits.ts): the schematic's dialog only trims.
    const edit = (e: ConnEdit): void => { applyConnEdit({ drawing: ctx.drawing }, conn, e); };
    if (f.installedBy) edit({ kind: 'installedBy', by: f.installedBy });
    if (f.cable != null) edit({ kind: 'label', text: f.cable.trim() });
    if (f.length !== undefined) edit({ kind: 'length', mm: length ?? null });
    if (f.lines != null) edit({ kind: 'lines', n: f.lines });
  } else if (hand) {
    if (f.installedBy) put(hand, 'installedBy', f.installedBy, 'stenal');
    if (f.cable != null) put(hand, 'cable', f.cable.trim() || null);
    if (f.length !== undefined) put(hand, 'length', length);
    if (lines !== undefined) { put(hand, 'lines', lines, 1); dropWireEdits(sch, w.first ?? key, lines); }
    if (f.ends) { hand.from = f.ends.from; hand.to = f.ends.to; }
  } else {
    if (f.installedBy) put(head(), 'installedBy', f.installedBy, w.kind === 'prep' ? 'client' : 'stenal');
    if (f.cable != null) put(head(), 'cable', f.cable.trim() || null);
    if (f.length !== undefined) put(head(), 'length', length);
    if (lines !== undefined) { put(head(), 'lines', lines, 1); dropWireEdits(sch, w.first ?? key, lines); }
    if (f.fed !== undefined) put(head(), 'to', f.fed || null, f.fed ? ownFeed(ctx, w) : undefined);
  }
  if (f.straight !== undefined) put(own(), 'straight', f.straight || null);
  if (f.jog !== undefined) put(own(), 'jog', f.jog == null ? null : clampElecJog(f.jog));
  if (f.thickness !== undefined) {
    const t = f.thickness == null ? null : Math.max(0.05, f.thickness);
    put(own(), 'thickness', t == null || Math.abs(t - m.wireThickness) < 1e-9 ? null : t);
  }
  if (f.style !== undefined) put(own(), 'style', f.style, m.wireStyle);
  if (f.color !== undefined) {
    const by = f.installedBy ?? w.installedBy, v = f.color?.toLowerCase();
    put(own(), 'color', v === ELEC_INSTALLER_COLORS[by].toLowerCase() ? null : v);
  }
}

/** After a box, a part or a column is shown again (`ElecSchematicNow.edit`):
 *  the client-row boxes it brings back (not in `was`, the row before it) put
 *  where no view reads them, nor any other box, as moved — `hiddenPlace`, by
 *  every view laid out with them shown (`ctx.clientAuto`) — while the row keeps
 *  a hand order, all of it dropped once every view stands it so by itself; a
 *  row with none (each view its own order) takes theirs off. Hidden, a client
 *  box has no place drawn (gone with its part or column, not even the
 *  part's), so it is placed once it has one: an order written meanwhile, or
 *  one kept while the row's were dropped, once brought it back first, the box
 *  beside it `moved`. A drag mark it kept is dropped when a box shown
 *  meanwhile has a later one. */
export function elecSettleShown(ctx: ElecEditContext, was: ReadonlySet<string>): void {
  const m = ctx.model, sch = ctx.drawing.elecSchematic, row = cellKeys(m, CLIENT_CELL);
  const back = row.filter((k) => !was.has(k)), views = ctx.clientAuto?.() ?? [];
  if (!sch || !back.length || !row.some((k) => m.boxByKey.get(k)?.order != null)) return;
  if (!row.some((k) => was.has(k) && m.boxByKey.get(k)?.order != null)) { for (const k of back) dropHandOrder(sch, k); return; }
  if (!views.length) return;
  // A drag mark it kept while out is older than one a box shown meanwhile got.
  const later = row.some((k) => was.has(k) && m.boxByKey.get(k)?.dragged);
  if (later) for (const k of back) delete sch.boxes?.[k]?.dragged;
  const home = (k: string): boolean => m.boxByKey.get(k)?.autoCell === CLIENT_CELL;
  const dragged = (k: string): boolean => !!m.boxByKey.get(k)?.dragged && !(later && !was.has(k));
  let all = row.filter((k) => was.has(k));
  for (const k of back) {
    const at = all.filter((x) => row.indexOf(x) < row.indexOf(k)).length;
    all = insertAt(all, hiddenPlace(all, k, at, views, home, dragged), k);
  }
  orderCell(sch, m, CLIENT_CELL, all, clientInAuto(clientPairs(ctx), all));
}

/** Make `edit` (nothing when it is refused). Returns the key of the box or wire
 *  it added, else null. */
export function applyElecEdit(ctx: ElecEditContext, edit: ElecEdit): string | null {
  if (edit.kind !== 'wireEnd' && elecEditRefusal(ctx, edit)) return null;
  const m = ctx.model, sch = (ctx.drawing.elecSchematic ??= {});
  adopt(sch, m);
  let made: string | null = null;
  switch (edit.kind) {
    case 'border': {
      const list = (sch.borders ??= []);
      let b = list.find((x) => x.row === edit.row && x.after === edit.after);
      if (!b) { b = { row: edit.row, after: edit.after }; list.push(b); }
      if (edit.dx !== undefined) put(b, 'dx', edit.dx, 0);
      if (edit.removed !== undefined) put(b, 'removed', edit.removed || null);
      if (b.dx == null && !b.removed) sch.borders = list.filter((x) => x !== b);
      break;
    }
    case 'boxAdd': {
      // No text: it reads "New box" in the drawing's language.
      (sch.handBoxes ??= []).push({ id: edit.id, cell: edit.cell, text: '' });
      made = ELEC_KEY.hand(edit.id);
      // Where it was put: an order written for the cell unless that is its
      // automatic one (a hand box sorts last, by its text).
      const base = dropOrder(m, edit.cell, edit.drawn), keys = insertAt(base, edit.index ?? Infinity, made);
      const fresh = { key: made, kind: 'hand', category: 'hand', name: ctx.tr?.(ELEC_NEW_BOX) ?? ELEC_NEW_BOX, cell: edit.cell, autoCell: edit.cell } as ElecBox;
      if (edit.cell !== CLIENT_CELL) { orderCell(sch, m, edit.cell, keys, undefined, fresh, made); break; }
      // The client row: last of a row with no hand order, wired to nothing, it
      // stands last as it is; anywhere else the row's order as drawn is written,
      // the boxes the view does not show where the other views stand them.
      const last = (edit.index ?? Infinity) >= base.length && !base.some((k) => m.boxByKey.get(k)?.order != null);
      if (!(last && inAutoOrder(m, [...cellKeys(m, edit.cell), made], fresh))) orderCell(sch, m, edit.cell, clientSettled(keys, edit.auto, made, clientPairs(ctx, edit.auto)), false, fresh, made);
      break;
    }
    case 'boxHidden': {
      const box = m.boxByKey.get(edit.key);
      if (!edit.hidden) delete sch.boxes?.[edit.key]?.hidden;
      else if (box?.kind === 'hand') {
        // Deleted, with what it leaves: its edits, the order it made its cell
        // take, the wires drawn to it and theirs.
        sch.handBoxes = (sch.handBoxes ?? []).filter((h) => h.id !== box.handId);
        delete sch.boxes?.[edit.key];
        const gone = (sch.handWires ?? []).filter((h) => h.from === edit.key || h.to === edit.key);
        for (const h of gone) dropWireEdits(sch, ELEC_KEY.handWire(h.id));
        if (gone.length) sch.handWires = sch.handWires!.filter((h) => !gone.includes(h));
        const left = cellKeys(m, box.cell).filter((k) => k !== edit.key);
        tidyCell(sch, m, box.cell, left, box.cell === CLIENT_CELL ? clientInAuto(clientPairs(ctx), left) : undefined);
      } else boxEdit(sch, edit.key).hidden = true;
      break;
    }
    case 'boxHome': {
      const box = m.boxByKey.get(edit.key);
      if (!box) break;
      const e = boxEdit(sch, edit.key);
      delete e.cell; dropHandOrder(sch, edit.key);
      // Before the first box of its own cell that stands in its place and comes
      // after it in the automatic order — the client row's as the view stands
      // it by itself; a client row with no other hand order stands it there.
      const others = cellKeys(m, box.autoCell).filter((k) => k !== edit.key);
      const after = box.autoCell === CLIENT_CELL || box.cell === CLIENT_CELL ? clientPairs(ctx, edit.auto) : null;
      if (box.autoCell === CLIENT_CELL) {
        if (others.some((k) => m.boxByKey.get(k)?.order != null)) { const keys = clientHome(m, edit.key, others, edit.auto, after); orderCell(sch, m, CLIENT_CELL, keys, clientInAuto(after, keys)); }
      } else {
        const i = others.findIndex((k) => { const b = m.boxByKey.get(k)!; return !b.moved && compareElecBoxesAuto(box, b) < 0; });
        orderCell(sch, m, box.autoCell, insertAt(others, i < 0 ? others.length : i, edit.key));
      }
      if (box.cell !== box.autoCell) {
        const left = cellKeys(m, box.cell).filter((k) => k !== edit.key);
        tidyCell(sch, m, box.cell, left, box.cell === CLIENT_CELL ? clientInAuto(after, left) : undefined);
      }
      break;
    }
    case 'boxElectricalLoad': put(boxEdit(sch, edit.key), 'electricalLoad', edit.on || null); break;
    case 'boxListLoads': put(boxEdit(sch, edit.key), 'listLoads', edit.on || null); break;
    case 'boxMove': {
      const box = m.boxByKey.get(edit.key);
      if (!box) break;
      put(boxEdit(sch, edit.key), 'cell', edit.cell, box.autoCell);
      // The client row: written as the view drew it, the boxes it does not show
      // where the other views stand them; dropped once every view stands it so
      // by itself.
      const after = edit.cell === CLIENT_CELL || box.cell === CLIENT_CELL ? clientPairs(ctx, edit.auto) : null;
      let keys = insertAt(dropOrder(m, edit.cell, edit.drawn).filter((k) => k !== edit.key), edit.index, edit.key);
      if (edit.cell === CLIENT_CELL) keys = clientSettled(keys, edit.auto, edit.key, after);
      orderCell(sch, m, edit.cell, keys, edit.cell === CLIENT_CELL ? clientInAuto(after, keys) : undefined, undefined, edit.key);
      if (box.cell !== edit.cell) {
        const left = cellKeys(m, box.cell).filter((k) => k !== edit.key);
        tidyCell(sch, m, box.cell, left, box.cell === CLIENT_CELL ? clientInAuto(after, left) : undefined);
      }
      break;
    }
    case 'boxPower': {
      // A hand box's own power; empty or none, it has none.
      const box = m.boxByKey.get(edit.key);
      const h = box?.kind === 'hand' ? sch.handBoxes?.find((x) => x.id === box.handId) : undefined;
      if (h) put(h, 'power', edit.w != null && Number.isFinite(edit.w) && edit.w > 0 ? Math.round(edit.w) : null);
      break;
    }
    case 'lightRun': {
      // A linear light's run one box with one wire, or one per LED: every light
      // of it marked, so that one of them gone the rest still is. Returns the
      // key of the box it is in now (the run's, or its first LED's).
      const run = (m.boxByKey.get(edit.key) ?? m.hiddenBoxes.find((b) => b.key === edit.key))?.run;
      if (!run || run.whole === edit.whole) break;
      const set = new Set(sch.wholeLights ?? []);
      for (const id of run.ids) if (edit.whole) set.add(id); else set.delete(id);
      if (set.size) sch.wholeLights = [...set]; else delete sch.wholeLights;
      made = buildElecSchematic(ctx.drawing as Drawing, { tr: ctx.tr }).boxes.find((b) => b.run?.ids.includes(run.ids[0]))?.key ?? null;
      break;
    }
    case 'boxText': {
      const box = m.boxByKey.get(edit.key);
      if (!box) break;
      const v = edit.text.replace(/\s+$/, '');
      // Entered as typed; empty, its own again (a hand box's: "New box").
      if (box.kind === 'hand') {
        const h = sch.handBoxes?.find((x) => x.id === box.handId);
        if (h) h.text = v.trim() ? v : '';
        break;
      }
      put(boxEdit(sch, edit.key), 'text', v.trim() ? v : null);
      break;
    }
    case 'boxTurn': {
      const next = ((((edit.box.angle + edit.dir * 90) % 360) + 360) % 360) as ElecBoxAngle;
      put(boxEdit(sch, edit.box.key), 'rotation', next, edit.box.autoAngle);
      break;
    }
    case 'column': {
      const list = (sch.columns ??= []);
      let e = list.find((c) => c.id === edit.id);
      if (!e) { e = { id: edit.id }; list.push(e); }
      if (edit.name != null) enter(e, edit.name);
      if (edit.color) e.color = edit.color;
      if (edit.hidden !== undefined) put(e, 'hidden', edit.hidden || null);
      break;
    }
    case 'columnAdd': {
      // No name: it reads "New column" in the drawing's language.
      const n = m.columns.length + m.hiddenColumns.length;
      (sch.columns ??= []).push({ id: edit.id, hand: true, color: ELEC_COLUMN_COLORS[n % ELEC_COLUMN_COLORS.length] });
      edit.view?.columns?.push(edit.id);
      break;
    }
    case 'columnMove': {
      const ids = m.columns.map((c) => c.id);
      swap(ids, ids.indexOf(edit.id), edit.by);
      sch.columnOrder = [...ids, ...m.hiddenColumns.map((c) => c.id)];
      break;
    }
    case 'columnRemove': {
      sch.columns = (sch.columns ?? []).filter((c) => c.id !== edit.id);
      if (edit.view?.columns) edit.view.columns = edit.view.columns.filter((c) => c !== edit.id);
      break;
    }
    case 'plantRoom': {
      const p = plantRooms(sch).find((x) => x.id === edit.id);
      if (p) enter(p, edit.name);
      break;
    }
    case 'plantRoomAdd': plantRooms(sch).push({ id: edit.id }); break;
    case 'plantRoomMove': { const list = plantRooms(sch); swap(list, list.findIndex((p) => p.id === edit.id), edit.by); break; }
    case 'plantRoomRemove': {
      const list = plantRooms(sch);
      if (list.length > 1) sch.plantRooms = list.filter((p) => p.id !== edit.id);
      break;
    }
    case 'rowHeight': {
      const h = (sch.rowHeights ??= {});
      if (edit.h == null) delete h[edit.row]; else h[edit.row] = Math.max(0, edit.h);
      break;
    }
    case 'rowHidden': {
      const set = new Set(sch.hiddenRows ?? []);
      if (edit.hidden) set.add(edit.row); else set.delete(edit.row);
      sch.hiddenRows = ELEC_ROWS.filter((r) => set.has(r));
      break;
    }
    case 'viewAside': {
      const v = edit.view, which = edit.which;
      if (edit.at === null) { delete v[which]; break; }
      if (edit.at) v[which] = { ...v[which], x: edit.at.x, y: edit.at.y };
      if (edit.hidden === false) delete v[which]?.hidden;
      else if (edit.hidden && v[which]) v[which]!.hidden = true;
      break;
    }
    case 'viewColumn': {
      // Every column, those hidden on the schematic too: a view keeps them, to
      // show them again when they are. A view with neither list showed them all;
      // ticked, a column moves from what it leaves out to what it shows, and back.
      const known = [...m.columns, ...m.hiddenColumns].map((c) => c.id);
      const v = edit.view;
      const shows = new Set(v.columns ?? (v.leaves ? [] : known)), leaves = new Set(v.leaves ?? []);
      if (edit.shown) { shows.add(edit.id); leaves.delete(edit.id); } else { shows.delete(edit.id); leaves.add(edit.id); }
      if (!leaves.size && known.every((x) => shows.has(x))) { delete v.columns; delete v.leaves; break; }
      // And an id the model has no column for now (a room with no box yet).
      const keep = (set: Set<string>, was?: string[]): string[] => [...known.filter((x) => set.has(x)), ...(was ?? []).filter((x) => !known.includes(x) && set.has(x))];
      v.columns = keep(shows, v.columns);
      v.leaves = keep(leaves, v.leaves);
      break;
    }
    case 'viewNumbers': put(edit.view, 'showNumbers', edit.shown ? null : false); break;
    case 'wire': setWireFigures(ctx, sch, edit.key, edit.figures); break;
    case 'wireAdd': {
      // Between two parts: a connection of the drawing (its id `con-<id>`).
      const conn = elecPartConnection(ctx.drawing, m, edit.from, edit.to, `con-${edit.id}`);
      if (conn) { ctx.drawing.connections.push(conn); made = ELEC_KEY.conn(conn.id); break; }
      // Else the schematic's own: the next S number, never given again.
      const n = sch.nextHandWire ?? Math.max(0, ...(sch.handWires ?? []).map((w) => w.n)) + 1;
      (sch.handWires ??= []).push({ id: edit.id, n, from: edit.from, to: edit.to });
      sch.nextHandWire = n + 1;
      made = ELEC_KEY.handWire(edit.id);
      break;
    }
    case 'wireEnd': reconnect(ctx, edit, true); break;
    case 'wireLook': {
      if (edit.thickness != null) put(sch, 'wireThickness', Math.max(0.05, edit.thickness), ELEC_WIRE_THICKNESS);
      if (edit.style) put(sch, 'wireStyle', edit.style, 'solid');
      break;
    }
    case 'wireRemoved': {
      const w = m.wires.find((x) => x.key === edit.key);
      if (!edit.removed) delete sch.wires?.[edit.key]?.removed;
      else if (w?.kind === 'hand' && !m.wires.some((x) => x.handId === w.handId && x.key !== w.key)) {
        // A hand wire's last cable shown: the wire deleted, with its edits. One
        // line of several is left out as any wire's is.
        sch.handWires = (sch.handWires ?? []).filter((h) => h.id !== w.handId);
        dropWireEdits(sch, w.first ?? edit.key);
      }
      else wireEdit(sch, edit.key).removed = true;
      break;
    }
    default: unreachable(edit, 'schematic edit');
  }
  prune(sch, m);
  return made;
}
